import { amplitudeLib } from 'freedom-shared';

/**
 * Дебаг + конверсионная воронка виджета ai-chat («Фрида»). `avfree_ai_chat_open`
 * живёт в main-page.ts (открытие с главной уже трекалось до этого набора) —
 * здесь события того, что происходит ПОСЛЕ открытия: ввод, дискавери-акинатор,
 * букинг (оффер → тариф → пассажиры → оплата → билет), ошибки, сессии.
 */

// Этап воронки на момент закрытия шторки — для анализа, на каком шаге чат
// бросают (используется как cross-tab funnel в Amplitude).
export type AiChatFunnelStage =
  | 'opened'
  | 'messaging'
  | 'discover_started'
  | 'destination_selected'
  | 'offer_shown'
  | 'offer_selected'
  | 'fare_shown'
  | 'fare_selected'
  | 'passengers_shown'
  | 'passengers_confirmed'
  | 'payment_shown'
  | 'payment_started'
  | 'ticket_issued';

export const aiChatClose = (params: {
  stage: AiChatFunnelStage;
  messages_count: number;
  had_error: boolean;
}) => {
  amplitudeLib.logEvent('avfree_ai_chat_close', params);
};

// ---- Ввод пользователя ----

// Откуда пришёл текст сообщения: набран руками, тап по статичной подсказке
// пустого экрана (`suggestion`), тап по подсказке-ответу от бэкенда (`chip`),
// выбор дат боевым календарём (`calendar`) или повтор упавшего хода.
export type AiChatMessageContext =
  | 'typed'
  | 'suggestion'
  | 'chip'
  | 'retry'
  | 'calendar';

export const aiChatMessageSent = (params: {
  context: AiChatMessageContext;
  is_first_message: boolean;
  text_length: number;
}) => {
  amplitudeLib.logEvent('avfree_ai_chat_message_sent', params);
};

// Бэкенд сгенерировал подсказки-ответы и они показаны в ленте. Пара к
// `avfree_ai_chat_message_sent` с `context: 'chip'`: показ против тапа отвечает
// на вопрос, попадают ли чипсы в то, что пользователь хотел сказать.
export const aiChatReplyChipsShown = (params: { chips_count: number }) => {
  amplitudeLib.logEvent('avfree_ai_chat_reply_chips_shown', params);
};

export const aiChatRetryClick = () => {
  amplitudeLib.logEvent('avfree_ai_chat_retry_click');
};

// Пользователь оборвал ход кнопкой в панели ввода. `stage` — этап воронки, на
// котором ответ надоело ждать: пара к `avfree_ai_chat_error` со `stage:
// 'stream'` отвечает, где генерация тянется настолько, что её бросают руками.
export const aiChatTurnStop = (params: { stage: AiChatFunnelStage }) => {
  amplitudeLib.logEvent('avfree_ai_chat_turn_stop', params);
};

// Бэкенд предложил выбрать даты (блок `calendar`) и в ленте показалась кнопка
// боевого календаря. Пара к `avfree_ai_chat_dates_picked`: показ против выбора
// отвечает, доходит ли предложение бэкенда до реального выбора дат.
export const aiChatDatePickerShown = () => {
  amplitudeLib.logEvent('avfree_ai_chat_date_picker_shown');
};

// Даты выбраны в календаре и ушли боту сообщением. `days_ahead` — от сегодня
// до вылета, `nights` — длительность поездки (0 для одной даты).
export const aiChatDatesPicked = (params: {
  is_range: boolean;
  days_ahead: number;
  nights: number;
}) => {
  amplitudeLib.logEvent('avfree_ai_chat_dates_picked', params);
};

// ---- Дискавери (акинатор «спланируем поездку») ----

export type AiChatDiscoverTrigger = 'plan_trip_phrase' | 'restart';

export const aiChatDiscoverStart = (params: {
  trigger: AiChatDiscoverTrigger;
}) => {
  amplitudeLib.logEvent('avfree_ai_chat_discover_start', params);
};

export type AiChatDiscoverStepType = 'generic' | 'visa' | 'budget';

export const aiChatDiscoverStep = (params: {
  step_type: AiChatDiscoverStepType;
}) => {
  amplitudeLib.logEvent('avfree_ai_chat_discover_step', params);
};

export const aiChatDiscoverResultsShown = (params: {
  results_count: number;
}) => {
  amplitudeLib.logEvent('avfree_ai_chat_discover_results_shown', params);
};

export const aiChatDiscoverRegenerate = () => {
  amplitudeLib.logEvent('avfree_ai_chat_discover_regenerate');
};

export const aiChatDiscoverRestart = () => {
  amplitudeLib.logEvent('avfree_ai_chat_discover_restart');
};

export const aiChatDestinationSelect = (params: {
  destination_code: string;
}) => {
  amplitudeLib.logEvent('avfree_ai_chat_destination_select', params);
};

// ---- Букинг (оффер → тариф → пассажиры → оплата → билет) ----

export const aiChatOfferShown = (params: { offer_id: string }) => {
  amplitudeLib.logEvent('avfree_ai_chat_offer_shown', params);
};

export const aiChatOfferSelect = (params: { offer_id: string }) => {
  amplitudeLib.logEvent('avfree_ai_chat_offer_select', params);
};

export const aiChatFareShown = (params: {
  offer_id: string;
  fare_count: number;
}) => {
  amplitudeLib.logEvent('avfree_ai_chat_fare_shown', params);
};

export const aiChatFareSelect = (params: {
  offer_id: string;
  fare_id: string;
}) => {
  amplitudeLib.logEvent('avfree_ai_chat_fare_select', params);
};

export const aiChatPassengersShown = (params: { passengers_count: number }) => {
  amplitudeLib.logEvent('avfree_ai_chat_passengers_shown', params);
};

export const aiChatPassengersConfirm = (params: { selected_count: number }) => {
  amplitudeLib.logEvent('avfree_ai_chat_passengers_confirm', params);
};

export const aiChatPaymentCardShown = (params: {
  order_id: string;
  has_bill_id: boolean;
}) => {
  amplitudeLib.logEvent('avfree_ai_chat_payment_card_shown', params);
};

export const aiChatPaymentStart = (params: { has_bill_id: boolean }) => {
  amplitudeLib.logEvent('avfree_ai_chat_payment_start', params);
};

// Отмена брони с карточки оплаты (действие 'cancel' из payment_card.actions).
export const aiChatBookingCancel = (params: { order_id: string }) => {
  amplitudeLib.logEvent('avfree_ai_chat_booking_cancel', params);
};

export const aiChatPaymentResult = (params: {
  success: boolean;
  error_message?: string;
}) => {
  amplitudeLib.logEvent('avfree_ai_chat_payment_result', params);
};

export const aiChatTicketIssued = (params: {
  order_id: string;
  order_number?: string;
}) => {
  amplitudeLib.logEvent('avfree_ai_chat_ticket_issued', params);
};

// ---- Кросс-сейл отелей (после оформления билета) ----

export const aiChatHotelOffersShown = (params: {
  destination: string;
  hotels_count: number;
}) => {
  amplitudeLib.logEvent('avfree_ai_chat_hotel_offers_shown', params);
};

export const aiChatHotelSelect = (params: {
  hotel_id: string;
  // false — у отеля нет диплинка, тап уходит в чат текстом.
  has_deep_link: boolean;
}) => {
  amplitudeLib.logEvent('avfree_ai_chat_hotel_select', params);
};

// ---- События-афиша (ветка вдохновения «хочу на концерт») ----

export const aiChatEventCardShown = (params: {
  event_title: string;
  destination: string;
}) => {
  amplitudeLib.logEvent('avfree_ai_chat_event_card_shown', params);
};

export const aiChatEventCardSelect = (params: {
  event_title: string;
  destination: string;
}) => {
  amplitudeLib.logEvent('avfree_ai_chat_event_card_select', params);
};

// ---- Ошибки (дебаг-канал, отдельно от воронки конверсии) ----

export type AiChatErrorStage =
  | 'stream'
  // Подключение к фоновой генерации открытого чата (GET .../stream?after=N).
  | 'stream_attach'
  // Ход оборвался на транспорте (свёрнутое приложение, потеря сети) и ушёл в
  // догон, а не в карточку ошибки (см. lib/turn-recovery.ts).
  | 'stream_interrupted'
  // Сообщение отбито 409 `thread_busy`: тред ещё занят предыдущим ходом (чаще
  // всего оборванным кнопкой «стоп» — отмены хода у concierge нет). Уходит в
  // ожидание с повторной отправкой, а не в карточку ошибки.
  | 'stream_busy'
  | 'payment'
  | 'sessions_load'
  // Догрузка следующей страницы списка чатов (GET /threads?cursor=…).
  | 'sessions_load_more'
  | 'plans_load'
  // Сохранение плана поездки (POST /trip-plans).
  | 'plan_save'
  | 'session_switch'
  // Перечитывание ленты из истории треда после события живого канала
  // (GET /threads/{id}/messages) — карточку после оплаты собирает бэкенд.
  | 'thread_events_refresh';

export const aiChatError = (params: {
  stage: AiChatErrorStage;
  message: string;
}) => {
  amplitudeLib.logEvent('avfree_ai_chat_error', params);
};

// ---- Сессии (история тредов) ----

export const aiChatHistoryOpen = () => {
  amplitudeLib.logEvent('avfree_ai_chat_history_open');
};

export const aiChatSessionNew = () => {
  amplitudeLib.logEvent('avfree_ai_chat_session_new');
};

export const aiChatSessionSwitch = () => {
  amplitudeLib.logEvent('avfree_ai_chat_session_switch');
};

// Открытие сохранённого плана поездки из сайдбара. plan_id — id плана на
// бэкенде (trip_plans.id), а не треда: план переживает чат, в котором родился.
export const aiChatTripPlanOpen = (params: { plan_id: string }) => {
  amplitudeLib.logEvent('avfree_ai_chat_trip_plan_open', params);
};

// Сохранение плана поездки из ленты в «Планы поездок» (POST /trip-plans).
// plan_id здесь нет: бэкенд создаёт план по координатам виджета и id отдаёт
// только в списке — в событии остаётся направление.
export const aiChatTripPlanSave = (params: { destination: string }) => {
  amplitudeLib.logEvent('avfree_ai_chat_trip_plan_save', params);
};
